import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import commissionService from '../../services/ShipmentCommissionService.js';
import AddPaymentModal from './commission_payment/AddPaymentModal.js';
import { DollarSign, Plus, MapPin, Eye, Pencil, Trash2, CreditCard } from 'lucide-react';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const CommissionList = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({ search: '', payment_status: '' });
  const navigate = useNavigate();
  const [isAddPaymentOpen, setIsAddPaymentOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const perPage = 20;

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchCommissions(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchCommissions(currentPage);
  }, [currentPage]);

  const fetchCommissions = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.search) filters.ml_sheet_id = filterValues.search;
      if (filterValues.payment_status) filters.payment_status = filterValues.payment_status;

      const response = await commissionService.getAllCommissions(page, perPage, filters);
      setCommissions(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      Alert.error('Failed to load commissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (commissionId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this commission? This action cannot be undone if payments exist.',
      'Delete Commission'
    );
    if (confirmed) {
      try {
        await commissionService.deleteCommission(commissionId);
        Alert.success('Commission deleted successfully!');
        if (commissions.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchCommissions();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete commission');
      }
    }
  };

  const getHubTotals = (commission) => {
    const senderHubs = Array.isArray(commission.sender_hub_commissions) ? commission.sender_hub_commissions : [];
    const receiverHubs = Array.isArray(commission.receiver_hub_commissions) ? commission.receiver_hub_commissions : [];
    return {
      senderCount: senderHubs.length,
      receiverCount: receiverHubs.length,
    };
  };

  const columns = [
    {
      key: 'commission_id',
      label: 'Comm. ID',
      render: (commission) => (
        <button
          onClick={() => navigate(`/commissions/view/${commission.commission_id}`)}
          className="font-medium text-brand hover:underline text-left"
        >
          {commission.commission_id}
        </button>
      ),
    },
    { key: 'ml_sheet_id', label: 'ML Sheet' },
    {
      key: 'total_freight',
      label: 'Total Freight',
      render: (c) => `₹${parseFloat(c.total_freight || 0).toLocaleString('en-IN')}`,
    },
    {
      key: 'company_commission',
      label: 'Company Comm.',
      render: (c) => (
        <div>
          <div>₹{parseFloat(c.company_commission_amount || 0).toLocaleString('en-IN')}</div>
          <div className="text-gray-400 text-xs">{parseFloat(c.company_commission_percent || 0).toFixed(0)}%</div>
        </div>
      ),
    },
    {
      key: 'company_status',
      label: 'Comp. Status',
      render: (c) => (
        <div>
          <StatusBadge status={c.company_payment_status} />
          <div className="text-gray-400 text-xs mt-1">
            Paid: ₹{parseFloat(c.company_paid_amount || 0).toLocaleString('en-IN')}
          </div>
        </div>
      ),
    },
    {
      key: 'owner_commission',
      label: 'Owner Comm.',
      render: (c) => (
        <div>
          <div>₹{parseFloat(c.vehicle_owner_commission_amount || 0).toLocaleString('en-IN')}</div>
          <div className="text-gray-400 text-xs">{parseFloat(c.vehicle_owner_commission_percent || 0).toFixed(0)}%</div>
        </div>
      ),
    },
    {
      key: 'owner_status',
      label: 'Owner Status',
      render: (c) => (
        <div>
          <StatusBadge status={c.vehicle_owner_payment_status} />
          <div className="text-gray-400 text-xs mt-1">
            Paid: ₹{parseFloat(c.vehicle_owner_paid_amount || 0).toLocaleString('en-IN')}
          </div>
        </div>
      ),
    },
    {
      key: 'sender_hubs',
      label: 'Sender Hubs',
      render: (c) => {
        const hubTotals = getHubTotals(c);
        const senderHubs = Array.isArray(c.sender_hub_commissions) ? c.sender_hub_commissions : [];
        return (
          <div>
            <div className="font-semibold">{hubTotals.senderCount} hub{hubTotals.senderCount !== 1 ? 's' : ''}</div>
            {hubTotals.senderCount > 0 && (
              <div className="text-gray-500 text-xs">
                ₹{senderHubs.reduce((s, h) => s + parseFloat(h.hub_commission_amount || 0), 0).toLocaleString('en-IN')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'receiver_hubs',
      label: 'Receiver Hubs',
      render: (c) => {
        const hubTotals = getHubTotals(c);
        const receiverHubs = Array.isArray(c.receiver_hub_commissions) ? c.receiver_hub_commissions : [];
        return (
          <div>
            <div className="font-semibold">{hubTotals.receiverCount} hub{hubTotals.receiverCount !== 1 ? 's' : ''}</div>
            {hubTotals.receiverCount > 0 && (
              <div className="text-gray-500 text-xs">
                ₹{receiverHubs.reduce((s, h) => s + parseFloat(h.hub_commission_amount || 0), 0).toLocaleString('en-IN')}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const getActions = (commission) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/commissions/view/${commission.commission_id}`),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/commissions/edit/${commission.commission_id}`),
    },
    {
      label: 'Add Payment',
      icon: CreditCard,
      onClick: () => {
        setSelectedCommission(commission);
        setIsAddPaymentOpen(true);
      },
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(commission.commission_id),
    },
  ];

  return (
    <>
      <PageLayout
        title="Commissions"
        icon={DollarSign}
        actions={
          <>
            <Button variant="secondary" icon={MapPin} onClick={() => navigate('/commissions/hub-insights')}>
              Hub Insights
            </Button>
            <Button icon={Plus} onClick={() => navigate('/commissions/add')}>
              Add Commission
            </Button>
          </>
        }
      >
        <FilterPanel
          filters={[
            { key: 'search', label: 'ML Sheet ID', placeholder: 'Search by ML Sheet ID...' },
            {
              key: 'payment_status',
              label: 'Payment Status',
              type: 'select',
              options: [
                { value: 'PENDING', label: 'Pending' },
                { value: 'PARTIAL', label: 'Partial' },
                { value: 'PAID', label: 'Paid' },
              ],
            },
          ]}
          values={filterValues}
          onChange={setFilterValues}
          onClear={() => setFilterValues({ search: '', payment_status: '' })}
        />

        <DataTable
          columns={columns}
          data={commissions}
          loading={loading}
          emptyMessage="No commissions found"
          rowKey="commission_id"
          actions={getActions}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </PageLayout>

      {isAddPaymentOpen && selectedCommission && (
        <AddPaymentModal
          commissionId={selectedCommission.commission_id}
          commission={selectedCommission}
          onClose={() => {
            setIsAddPaymentOpen(false);
            setSelectedCommission(null);
          }}
          onSuccess={() => {
            setIsAddPaymentOpen(false);
            setSelectedCommission(null);
            fetchCommissions();
          }}
        />
      )}
    </>
  );
};

export default CommissionList;
